import { Formik, Form } from 'formik';

import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';
import { FormActions } from '@@/form-components/FormActions';

import { DockerSecurityFormData } from './types';

interface Props {
  initialValues: DockerSecurityFormData;
  onSubmit: (values: DockerSecurityFormData) => void | Promise<void>;
  /** Mode: 'create' | 'edit' */
  mode: 'create' | 'edit';
  /** Whether the policy name has been provided (for validation) */
  nameProvided: boolean;
  /** Whether the environment groups have been provided (for validation) */
  environmentGroupsProvided: boolean;
}

/**
 * Docker Security Policy Form Component
 *
 * Renders the security constraints form for Docker/Swarm/Podman policies.
 * Can be used for both creating new policies and editing existing ones.
 */
export function DockerSecurityPolicyForm({
  initialValues,
  onSubmit,
  mode,
  nameProvided,
  environmentGroupsProvided,
}: Props) {
  return (
    <Formik<DockerSecurityFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ values, setFieldValue, isValid, isSubmitting }) => (
        <Form>
          <FormSection title="Docker security settings">
            <SwitchField
              checked={!values.allowBindMountsForRegularUsers}
              onChange={(value) =>
                setFieldValue('allowBindMountsForRegularUsers', !value)
              }
              label="Hide bind mounts for non-administrators"
              tooltip="When enabled, regular users will not be able to use bind mounts when creating containers."
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-bind-mounts-switch"
            />
            <SwitchField
              checked={!values.allowPrivilegedModeForRegularUsers}
              onChange={(value) =>
                setFieldValue('allowPrivilegedModeForRegularUsers', !value)
              }
              label="Hide privileged mode for non-administrators"
              tooltip="When enabled, regular users will not be able to use privileged mode when creating containers."
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-privileged-mode-switch"
            />
            <SwitchField
              checked={!values.allowHostNamespaceForRegularUsers}
              onChange={(value) =>
                setFieldValue('allowHostNamespaceForRegularUsers', !value)
              }
              label="Hide the use of host PID 1 for non-administrators"
              tooltip="Prevent users from accessing the host filesystem through the host PID namespace."
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-host-namespace-switch"
            />
            <SwitchField
              checked={!values.allowStackManagementForRegularUsers}
              onChange={(value) =>
                setFieldValue('allowStackManagementForRegularUsers', !value)
              }
              label="Hide the use of Stacks for non-administrators"
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-stack-management-switch"
            />
            <SwitchField
              checked={!values.allowDeviceMappingForRegularUsers}
              onChange={(value) =>
                setFieldValue('allowDeviceMappingForRegularUsers', !value)
              }
              label="Hide device mappings for non-administrators"
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-device-mapping-switch"
            />
            <SwitchField
              checked={!values.allowContainerCapabilitiesForRegularUsers}
              onChange={(value) =>
                setFieldValue(
                  'allowContainerCapabilitiesForRegularUsers',
                  !value
                )
              }
              label="Hide container capabilities for non-administrators"
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-container-capabilities-switch"
            />
            <SwitchField
              checked={!values.allowSysctlSettingForRegularUsers}
              onChange={(value) =>
                setFieldValue('allowSysctlSettingForRegularUsers', !value)
              }
              label="Hide sysctl settings for non-administrators"
              labelClass="col-sm-7 col-lg-4"
              fieldClass="!mb-4"
              data-cy="disable-sysctl-setting-switch"
            />
          </FormSection>

          <FormActions
            submitLabel={mode === 'create' ? 'Create policy' : 'Update policy'}
            loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
            isLoading={isSubmitting}
            isValid={isValid && !!nameProvided && !!environmentGroupsProvided}
            data-cy="policy-form-actions"
          />
        </Form>
      )}
    </Formik>
  );
}
